import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, SegmentedControl, Skeleton, TextField } from "../components/ui";
import { fetchFoodMacrosBatch, searchFoods } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import type { FdcBatchMacrosResponse, FdcFoodSearchItem } from "../types";

type ExploreSource = "all" | "foundation" | "branded";

const sourceOptions: Array<{ value: ExploreSource; label: string }> = [
  { value: "all", label: "All" },
  { value: "foundation", label: "Foundation" },
  { value: "branded", label: "Branded" },
];

function macroKey(source: string | undefined, fdcId: number): string {
  return `${source ?? "unknown"}:${fdcId}`;
}

function servingLabel(item: FdcFoodSearchItem): string | null {
  if (!item.serving_size || !item.serving_size_unit) {
    return null;
  }
  return `${item.serving_size}${item.serving_size_unit}`;
}

export function ExplorePage() {
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<ExploreSource>("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<FdcFoodSearchItem[]>([]);
  const [macros, setMacros] = useState<Record<string, NonNullable<FdcBatchMacrosResponse["items"]>[number]>>({});

  const lastTrackedQueryRef = useRef("");

  async function runSearch(searchTerm: string, reset = true): Promise<void> {
    const trimmed = searchTerm.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setMacros({});
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await searchFoods(trimmed, source, 30, 0);
      const items = response.items ?? [];
      setResults(items);
      if (trimmed !== lastTrackedQueryRef.current) {
        lastTrackedQueryRef.current = trimmed;
        trackEvent("explore_search_executed", { query: trimmed, source, result_count: items.length });
      }

      const refs = items
        .filter((item) => (item.source ?? item.fdc_source) && item.fdc_id > 0)
        .slice(0, 30)
        .map((item) => ({ source: item.source ?? item.fdc_source ?? "all", fdcId: item.fdc_id }));

      if (refs.length > 0) {
        const macroResponse = await fetchFoodMacrosBatch(refs);
        const next: Record<string, NonNullable<FdcBatchMacrosResponse["items"]>[number]> = {};
        (macroResponse.items ?? []).forEach((entry) => {
          next[macroKey(entry.source, entry.fdc_id)] = entry;
        });
        setMacros(next);
      } else if (reset) {
        setMacros({});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to search food catalog.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const id = window.setTimeout(() => {
      void runSearch(query, true);
    }, 320);

    return () => {
      window.clearTimeout(id);
    };
  }, [query, source]);

  const hasQuery = query.trim().length >= 2;

  const starterSuggestions = useMemo(() => ["oatmeal", "bananas", "greek yogurt", "brown rice", "black beans"], []);

  return (
    <div data-testid="explore-page" className="space-y-4 pb-4">
      <PageHeader title="Explore" subtitle="Search USDA FDC foods and compare macro profiles." />

      <Card>
        <div className="space-y-3">
          <TextField label="Search foods" value={query} onChange={setQuery} placeholder="Search foods, brands, or ingredients" />
          <SegmentedControl value={source} options={sourceOptions} onChange={setSource} />
        </div>
      </Card>

      {!hasQuery ? (
        <Card>
          <h3 className="text-base font-semibold">Starter searches</h3>
          <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Try one of the popular pantry-friendly queries.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {starterSuggestions.map((item) => (
              <button key={item} type="button" className="pr-chip px-3 py-1 text-sm font-semibold" onClick={() => { trackEvent("explore_suggestion_clicked", { suggestion: item }); setQuery(item); }}>
                {item}
              </button>
            ))}
          </div>
        </Card>
      ) : loading ? (
        <div className="space-y-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void runSearch(query, false)} />
      ) : results.length === 0 ? (
        <EmptyState title="No food matches" subtitle="Try another keyword, ingredient, or source." />
      ) : (
        <div className="space-y-3">
          {results.map((item) => {
            const source = item.source ?? item.fdc_source ?? undefined;
            const key = macroKey(source, item.fdc_id);
            const macro = macros[key];
            return (
              <Card key={item.id} flat>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold">{item.description}</h3>
                    <p className="mt-1 text-sm text-[var(--pr-text-muted)]">{item.category || "No category"}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {source ? <Pill tone="accent" label={source.toUpperCase()} /> : null}
                      {servingLabel(item) ? <Pill label={servingLabel(item) as string} /> : null}
                      {macro?.calories_kcal ? <Pill label={`${Math.round(macro.calories_kcal)} kcal`} /> : null}
                    </div>
                  </div>

                  {source && item.fdc_id ? (
                    <Link
                      to={`/app/explore/${source}/${item.fdc_id}`}
                      className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold"
                      onClick={() => trackEvent("explore_food_selected", { source: source ?? "unknown", fdc_id: item.fdc_id })}
                    >
                      Open details
                    </Link>
                  ) : (
                    <Button variant="ghost" className="min-h-10 px-3 text-sm" disabled>
                      Missing source
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
